import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { API_CONFIG } from '../config/api';
import { authenticatedFetch, parseJsonResponse } from '../utils/apiHelper';
import DateRangeFilter from '../components/DateRangeFilter';

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, handleUnauthorized } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordersPagination, setOrdersPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [errorsPagination, setErrorsPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [contactsPagination, setContactsPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingEmails, setSendingEmails] = useState(new Set());
  const [sendingTelegram, setSendingTelegram] = useState(new Set());
  const [sendingViberBusiness, setSendingViberBusiness] = useState(new Set());
  const [contacts, setContacts] = useState([]);
  const [messengerConfig, setMessengerConfig] = useState(null);
  // Shared date range for all tabs (Orders, Error Logs, Contact Info)
  const [sharedDateRange, setSharedDateRange] = useState(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [emailSentFilter, setEmailSentFilter] = useState('');
  const [telegramSentFilter, setTelegramSentFilter] = useState('');
  const [discountFilter, setDiscountFilter] = useState('');
  const [selectedSoapLog, setSelectedSoapLog] = useState(null);
  const [soapLogModalOpen, setSoapLogModalOpen] = useState(false);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  const [settings, setSettings] = useState({
    can_sell: true,
    can_sell_holiday_season: true,
    can_sell_winter_season: true,
    can_sell_low_season: true,
    telegram_enabled: null,
    viber_business_enabled: null,
    softserve_discount_enabled: false,
    softserve_discount_percent: 10
  });
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [manualCheckLoading, setManualCheckLoading] = useState(false);
  const [manualCheckStats, setManualCheckStats] = useState(null);
  const [ordersPeriod, setOrdersPeriod] = useState('all'); // 'all', 'beforeProd', 'prod'
  const [errorsPeriod, setErrorsPeriod] = useState('all'); // 'all', 'beforeProd', 'prod'
  const [contactsPeriod, setContactsPeriod] = useState('all'); // 'all', 'beforeProd', 'prod'
  
  // Fixed dates for pre-prod and prod periods
  const preProdEndDate = new Date('2025-12-23T23:59:59.999Z'); // End of pre-prod: 23.12.2025 23:59:59
  const prodStartDate = new Date('2025-12-24T00:00:00.000Z'); // Start of prod: 24.12.2025 00:00:00
  const prodStartDateFormatted = '24.12'; // Display format for prod start date
  const preProdEndDateFormatted = '23.12'; // Display format for pre-prod end date
  
  // Get current period based on active tab (computed value, not function)
  const currentPeriod = activeTab === 'orders' ? ordersPeriod : 
                        activeTab === 'errors' ? errorsPeriod : 
                        activeTab === 'contacts' ? contactsPeriod : 'all';
  
  // Set current period - apply to all tabs simultaneously
  const setCurrentPeriod = (period) => {
    setOrdersPeriod(period);
    setErrorsPeriod(period);
    setContactsPeriod(period);
  };
  
  // Shared date range - same for all tabs (Orders, Error Logs, Contact Info)
  const currentDateRange = sharedDateRange;
  
  const setCurrentDateRange = (range) => {
    setSharedDateRange(range);
  };

  const fetchMessengerConfig = async () => {
    try {
      const apiUrl = API_CONFIG?.baseURL || '';
      const response = await fetch(`${apiUrl}/api/messengers/config`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          setMessengerConfig(data.config);
        }
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchMessengerConfig();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    if (activeTab === 'orders') {
      fetchStats();
      fetchOrders();
      if (!settings || Object.keys(settings).length === 0 || settings.can_sell === undefined) {
        fetchSettings();
      }
      if (!messengerConfig || !messengerConfig.telegram) {
        fetchMessengerConfig();
      }
    } else if (activeTab === 'errors') {
      fetchStats();
      fetchErrors();
    } else if (activeTab === 'contacts') {
      fetchStats();
      fetchContacts();
    } else if (activeTab === 'settings') {
      fetchSettings();
    } else {
      fetchStats();
    }
  }, [isAuthenticated, activeTab, ordersPagination.page, errorsPagination.page, contactsPagination.page, sharedDateRange, orderStatusFilter, paymentStatusFilter, emailSentFilter, telegramSentFilter, discountFilter, ordersPeriod, errorsPeriod, contactsPeriod]);

  // Socket.IO connection for real-time settings updates
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'settings') return;

    // Import socket.io-client dynamically
    import('socket.io-client').then(({ default: io }) => {
      // Use socket URL (can be different from API URL for CloudFront/Amplify)
      // Socket.IO should connect directly to backend, not through CloudFront
      const socketUrl = API_CONFIG.socketURL || API_CONFIG.baseURL || 'http://localhost:5001';
      
      // Don't connect to localhost in production
      if (API_CONFIG.isProduction && socketUrl.includes('localhost')) {
        console.warn('⚠️ Socket.IO: Cannot connect to localhost in production. Please set REACT_APP_SOCKET_URL or REACT_APP_API_BASE_URL environment variable.');
        return;
      }
      
      const socket = io(socketUrl, {
        // Use polling ONLY - bypass CloudFront, connect directly to backend
        // Polling is more reliable and doesn't require WebSocket support
        transports: ['polling'],
        // Disable upgrade to WebSocket - use polling only
        upgrade: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity, // Allow infinite reconnection attempts
        timeout: 20000,
        path: '/socket.io/',
        query: {},
        // Auto-connect: false to prevent automatic connection attempts
        autoConnect: true,
        // Force new connection on 400 errors (invalid session ID)
        rejectUnauthorized: false
      });

      socket.on('connect', () => {
        // Connected successfully - no log needed
      });
      
      socket.on('connect_error', (error) => {
        // "Session ID unknown" is expected when session expires or server restarts
        // Client will automatically reconnect with a new session
        const isSessionIdUnknown = error.message && (
          error.message.includes('Session ID unknown') ||
          error.message.includes('session ID unknown') ||
          error.message.includes('Unknown session') ||
          error.message.includes('xhr poll error')
        );
        
        // 400 Bad Request usually means invalid session ID
        const isBadRequest = error.type === 'TransportError' || 
                            (error.message && error.message.includes('400')) ||
                            (error.message && error.message.includes('Bad Request'));
        
        if (isSessionIdUnknown || isBadRequest) {
          // Force reconnection with new session ID
          if (socket.connected) {
            socket.disconnect();
          }
          // Client will automatically reconnect
          return;
        }
        
        // Silently handle other connection errors - Socket.IO is optional for settings updates
        // Don't show errors to user as this doesn't affect core functionality
        console.warn('Socket.IO connection error (non-critical):', error.message);
        // Disable reconnection after multiple failures to prevent spam
        if (socket.recovered) {
          socket.disconnect();
        }
      });

      socket.on('settings-updated', (updatedSettings) => {
        setSettings({
          can_sell: updatedSettings.can_sell ?? settings.can_sell,
          can_sell_holiday_season: updatedSettings.can_sell_holiday_season ?? settings.can_sell_holiday_season,
          can_sell_winter_season: updatedSettings.can_sell_winter_season ?? settings.can_sell_winter_season,
          can_sell_low_season: updatedSettings.can_sell_low_season ?? settings.can_sell_low_season,
          telegram_enabled: updatedSettings.telegram_enabled ?? settings.telegram_enabled,
          viber_business_enabled: updatedSettings.viber_business_enabled ?? settings.viber_business_enabled,
          softserve_discount_enabled: updatedSettings.softserve_discount_enabled ?? settings.softserve_discount_enabled,
          softserve_discount_percent: updatedSettings.softserve_discount_percent ?? settings.softserve_discount_percent
        });
      });

      socket.on('disconnect', () => {
        // Disconnected
      });
      
      // Log connection errors only
      socket.on('connect_error', (error) => {
        console.error('❌ Socket.IO connection error:', error.message || error);
      });

      return () => {
        socket.disconnect();
      };
    }).catch(err => {
      console.error('Failed to load socket.io-client:', err);
    });
  }, [isAuthenticated, activeTab]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add date range based on selected period (use currentPeriod which is synced across tabs)
      // Pre-production start date: 2025-12-08
      const preProdStartDate = new Date('2025-12-08T00:00:00.000Z'); // 08.12.2025 00:00:00 UTC
      
      // Use currentPeriod which is synced across all tabs
      const periodToUse = currentPeriod;
      
      // Period filter (main filter) - defines base date range
      let periodStartDate = null;
      let periodEndDate = null;
      
      if (periodToUse === 'beforeProd') {
        // Pre-Prod: from 2025-12-08 till 23.12.2025 23:59:59
        periodStartDate = preProdStartDate;
        periodEndDate = preProdEndDate;
      } else if (periodToUse === 'prod') {
        // Prod: from 24.12.2025 00:00:00 onwards
        periodStartDate = prodStartDate;
        periodEndDate = null; // No end date limit
      }
      // For 'all' period: no period filter (periodStartDate and periodEndDate remain null)
      
      // Date range filter (secondary filter) - applies additional constraints within the period
      let finalStartDate = periodStartDate;
      let finalEndDate = periodEndDate;
      
      if (sharedDateRange?.startDate || sharedDateRange?.endDate) {
        // Apply date range filter as secondary filter
        if (sharedDateRange.startDate) {
          const filterStartDate = new Date(sharedDateRange.startDate);
          // For 'prod' period, ensure startDate is at least prodStartDate
          if (periodToUse === 'prod' && filterStartDate < prodStartDate) {
            finalStartDate = prodStartDate;
          } else {
            // Use the later date between period start and filter start
            finalStartDate = periodStartDate && filterStartDate < periodStartDate ? periodStartDate : filterStartDate;
          }
        } else if (periodStartDate) {
          // If no filter startDate but period has startDate, use period startDate
          finalStartDate = periodStartDate;
        }
        
        if (sharedDateRange.endDate) {
          const filterEndDate = new Date(sharedDateRange.endDate);
          // Use the earlier date between period end and filter end
          finalEndDate = periodEndDate && filterEndDate > periodEndDate ? periodEndDate : filterEndDate;
        } else if (periodEndDate) {
          // If no filter endDate but period has endDate, use period endDate
          finalEndDate = periodEndDate;
        }
      } else {
        // No date range filter - use period filter only
        finalStartDate = periodStartDate;
        finalEndDate = periodEndDate;
      }
      
      // Apply final date filters
      if (finalStartDate) {
        params.append('startDate', finalStartDate.toISOString());
      }
      if (finalEndDate) {
        params.append('endDate', finalEndDate.toISOString());
      }
      
      const url = `/api/admin/stats${params.toString() ? `?${params}` : ''}`;
      const response = await authenticatedFetch(url, {
        method: 'GET'
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setStats(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: ordersPagination.page,
        limit: ordersPagination.limit,
        ...(searchTerm && { search: searchTerm })
      });
      
      // Add date range based on selected period
      // Pre-production start date: 2025-12-08
      const preProdStartDate = new Date('2025-12-08T00:00:00.000Z'); // 08.12.2025 00:00:00 UTC
      
      // Period filter (main filter) - defines base date range
      let periodStartDate = null;
      let periodEndDate = null;
      
      if (ordersPeriod === 'beforeProd') {
        // Pre-Prod: from 2025-12-08 till 23.12.2025 23:59:59
        periodStartDate = preProdStartDate;
        periodEndDate = preProdEndDate;
      } else if (ordersPeriod === 'prod') {
        // Prod: from 24.12.2025 00:00:00 onwards
        periodStartDate = prodStartDate;
        periodEndDate = null; // No end date limit
      }
      // For 'all' period: no period filter (periodStartDate and periodEndDate remain null)
      
      // Date range filter (secondary filter) - applies additional constraints within the period
      let finalStartDate = periodStartDate;
      let finalEndDate = periodEndDate;
      
      if (sharedDateRange?.startDate || sharedDateRange?.endDate) {
        // Apply date range filter as secondary filter
        if (sharedDateRange.startDate) {
          const filterStartDate = new Date(sharedDateRange.startDate);
          // For 'prod' period, ensure startDate is at least prodStartDate
          if (ordersPeriod === 'prod' && filterStartDate < prodStartDate) {
            finalStartDate = prodStartDate;
          } else {
            // Use the later date between period start and filter start
            finalStartDate = periodStartDate && filterStartDate < periodStartDate ? periodStartDate : filterStartDate;
          }
        } else if (periodStartDate) {
          // If no filter startDate but period has startDate, use period startDate
          finalStartDate = periodStartDate;
        }
        
        if (sharedDateRange.endDate) {
          const filterEndDate = new Date(sharedDateRange.endDate);
          // Use the earlier date between period end and filter end
          finalEndDate = periodEndDate && filterEndDate > periodEndDate ? periodEndDate : filterEndDate;
        } else if (periodEndDate) {
          // If no filter endDate but period has endDate, use period endDate
          finalEndDate = periodEndDate;
        }
      } else {
        // No date range filter - use period filter only
        finalStartDate = periodStartDate;
        finalEndDate = periodEndDate;
      }
      
      // Apply final date filters
      if (finalStartDate) {
        params.append('startDate', finalStartDate.toISOString());
      }
      if (finalEndDate) {
        params.append('endDate', finalEndDate.toISOString());
      }
      
      // Add filter parameters
      if (orderStatusFilter) {
        params.append('status', orderStatusFilter);
      }
      if (paymentStatusFilter) {
        params.append('paymentStatus', paymentStatusFilter);
      }
      if (emailSentFilter !== '') {
        params.append('emailSent', emailSentFilter === 'true' ? 'true' : 'false');
      }
      if (telegramSentFilter !== '') {
        params.append('telegramSent', telegramSentFilter === 'true' ? 'true' : 'false');
      }
      if (discountFilter === 'discounted') {
        params.append('discountedOnly', 'true');
      }
      const response = await authenticatedFetch(`/api/admin/orders?${params}`, {
        method: 'GET'
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setOrders(data.data.orders);
          setOrdersPagination(prev => ({
            ...prev,
            total: data.data.pagination.total,
            pages: data.data.pagination.pages
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: contactsPagination.page.toString(),
        limit: contactsPagination.limit.toString()
      });
      
      // Add date range based on selected period
      const preProdStartDate = new Date('2025-12-08T00:00:00.000Z');
      
      // Period filter (main filter) - defines base date range
      let periodStartDate = null;
      let periodEndDate = null;
      
      if (contactsPeriod === 'beforeProd') {
        // Pre-Prod: from 2025-12-08 till 23.12.2025 23:59:59
        periodStartDate = preProdStartDate;
        periodEndDate = preProdEndDate;
      } else if (contactsPeriod === 'prod') {
        // Prod: from 24.12.2025 00:00:00 onwards
        periodStartDate = prodStartDate;
        periodEndDate = null; // No end date limit
      }
      // For 'all' period: no period filter (periodStartDate and periodEndDate remain null)
      
      // Date range filter (secondary filter) - applies additional constraints within the period
      let finalStartDate = periodStartDate;
      let finalEndDate = periodEndDate;
      
      if (sharedDateRange?.startDate || sharedDateRange?.endDate) {
        // Apply date range filter as secondary filter
        if (sharedDateRange.startDate) {
          const filterStartDate = new Date(sharedDateRange.startDate);
          // For 'prod' period, ensure startDate is at least prodStartDate
          if (contactsPeriod === 'prod' && filterStartDate < prodStartDate) {
            finalStartDate = prodStartDate;
          } else {
            // Use the later date between period start and filter start
            finalStartDate = periodStartDate && filterStartDate < periodStartDate ? periodStartDate : filterStartDate;
          }
        } else if (periodStartDate) {
          // If no filter startDate but period has startDate, use period startDate
          finalStartDate = periodStartDate;
        }
        
        if (sharedDateRange.endDate) {
          const filterEndDate = new Date(sharedDateRange.endDate);
          // Use the earlier date between period end and filter end
          finalEndDate = periodEndDate && filterEndDate > periodEndDate ? periodEndDate : filterEndDate;
        } else if (periodEndDate) {
          // If no filter endDate but period has endDate, use period endDate
          finalEndDate = periodEndDate;
        }
      } else {
        // No date range filter - use period filter only
        finalStartDate = periodStartDate;
        finalEndDate = periodEndDate;
      }
      
      // Apply final date filters
      if (finalStartDate) {
        params.append('startDate', finalStartDate.toISOString());
      }
      if (finalEndDate) {
        params.append('endDate', finalEndDate.toISOString());
      }
      
      const response = await authenticatedFetch(`/api/admin/contacts?${params}`, {
        method: 'GET'
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setContacts(data.data.contacts || []);
          setContactsPagination(prev => ({
            ...prev,
            total: data.data.pagination?.total || 0,
            pages: data.data.pagination?.pages || 0
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: errorsPagination.page,
        limit: errorsPagination.limit
      });
      
      // Add date range based on selected period
      const preProdStartDate = new Date('2025-12-08T00:00:00.000Z');
      
      // Period filter (main filter) - defines base date range
      let periodStartDate = null;
      let periodEndDate = null;
      
      if (errorsPeriod === 'beforeProd') {
        // Pre-Prod: from 2025-12-08 till 23.12.2025 23:59:59
        periodStartDate = preProdStartDate;
        periodEndDate = preProdEndDate;
      } else if (errorsPeriod === 'prod') {
        // Prod: from 24.12.2025 00:00:00 onwards
        periodStartDate = prodStartDate;
        periodEndDate = null; // No end date limit
      }
      // For 'all' period: no period filter (periodStartDate and periodEndDate remain null)
      
      // Date range filter (secondary filter) - applies additional constraints within the period
      let finalStartDate = periodStartDate;
      let finalEndDate = periodEndDate;
      
      if (sharedDateRange?.startDate || sharedDateRange?.endDate) {
        // Apply date range filter as secondary filter
        if (sharedDateRange.startDate) {
          const filterStartDate = new Date(sharedDateRange.startDate);
          // For 'prod' period, ensure startDate is at least prodStartDate
          if (errorsPeriod === 'prod' && filterStartDate < prodStartDate) {
            finalStartDate = prodStartDate;
          } else {
            // Use the later date between period start and filter start
            finalStartDate = periodStartDate && filterStartDate < periodStartDate ? periodStartDate : filterStartDate;
          }
        } else if (periodStartDate) {
          // If no filter startDate but period has startDate, use period startDate
          finalStartDate = periodStartDate;
        }
        
        if (sharedDateRange.endDate) {
          const filterEndDate = new Date(sharedDateRange.endDate);
          // Use the earlier date between period end and filter end
          finalEndDate = periodEndDate && filterEndDate > periodEndDate ? periodEndDate : filterEndDate;
        } else if (periodEndDate) {
          // If no filter endDate but period has endDate, use period endDate
          finalEndDate = periodEndDate;
        }
      } else {
        // No date range filter - use period filter only
        finalStartDate = periodStartDate;
        finalEndDate = periodEndDate;
      }
      
      // Apply final date filters
      if (finalStartDate) {
        params.append('startDate', finalStartDate.toISOString());
      }
      if (finalEndDate) {
        params.append('endDate', finalEndDate.toISOString());
      }
      const response = await authenticatedFetch(`/api/admin/errors?${params}`, {
        method: 'GET'
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setErrors(data.data.errors);
          setErrorsPagination(prev => ({
            ...prev,
            total: data.data.pagination.total,
            pages: data.data.pagination.pages
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch('/api/admin/settings', {
        method: 'GET'
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setSettings(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    setUpdatingSettings(true);
    try {
      const response = await authenticatedFetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      }, () => {
        handleUnauthorized();
        navigate('/admin/login');
      });
      
      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setSettings(prev => ({ ...prev, ...data.data }));
        }
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const ordersColumns = [
    {
      accessorKey: 'orderId',
      header: 'Order ID',
      cell: info => <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{info.getValue()}</span>
    },
    {
      accessorKey: 'customerInfo.email',
      header: 'Email',
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'customerInfo.firstName',
      header: 'Customer',
      cell: info => {
        const order = info.row.original;
        return `${order.customerInfo?.firstName || ''} ${order.customerInfo?.lastName || ''}`.trim() || '-';
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: info => {
        const status = info.getValue();
        const colors = {
          pending: '#f59e0b',
          paid: '#10b981',
          completed: '#3b82f6',
          failed: '#ef4444'
        };
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: colors[status] || '#6b7280',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {status}
          </span>
        );
      }
    },
    {
      accessorKey: 'paymentInfo.amount',
      header: 'Amount',
      cell: info => {
        const order = info.row.original;
        const amount = order.paymentInfo?.amount || order.totalAmount || 0;
        const currency = order.paymentInfo?.currency || 'UAH';
        const formattedCurrency = currency === 'UAH' ? 'грн' : currency;
        return amount ? `${amount.toFixed(2)} ${formattedCurrency}` : '-';
      }
    },
    {
      accessorKey: 'confirmationNumber',
      header: 'Confirmation #',
      cell: info => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.85em', fontWeight: 'bold' }}>
          {info.getValue() || '-'}
        </span>
      )
    },
    {
      accessorKey: 'orderIdSKIDATA',
      header: 'SKIDATA ID',
      cell: info => (
        <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
          {info.getValue() || '-'}
        </span>
      )
    },
    {
      accessorKey: 'isPaid',
      header: 'Paid',
      cell: info => {
        const isPaid = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: isPaid ? '#10b981' : '#ef4444',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {isPaid ? 'Yes' : 'No'}
          </span>
        );
      }
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Payment Status',
      cell: info => {
        const status = info.getValue();
        const colors = {
          success: '#10b981',
          pending: '#f59e0b',
          failed: '#ef4444',
          'in-progress': '#3b82f6',
          cancelled: '#6b7280',
          refunded: '#8b5cf6'
        };
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: colors[status] || '#6b7280',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {status || '-'}
          </span>
        );
      }
    },
    {
      accessorKey: 'is_skidata_order_success',
      header: 'SKIDATA Success',
      cell: info => {
        const success = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: success ? '#10b981' : '#ef4444',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {success ? 'Yes' : 'No'}
          </span>
        );
      }
    },
    {
      accessorKey: 'emailSent',
      header: 'Email Sent',
      cell: info => {
        const emailSent = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: emailSent ? '#10b981' : '#f59e0b',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {emailSent ? 'Yes' : 'No'}
          </span>
        );
      }
    },
    {
      accessorKey: 'emailSentAt',
      header: 'Email Sent At',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'telegramSent',
      header: 'TG Sent',
      cell: info => {
        const telegramSent = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: telegramSent ? '#10b981' : '#f59e0b',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {telegramSent ? 'Yes' : 'No'}
          </span>
        );
      }
    },
    {
      accessorKey: 'telegramSentAt',
      header: 'TG Sent At',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'viberBusinessSent',
      header: 'Viber Sent',
      cell: info => {
        const viberBusinessSent = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: viberBusinessSent ? '#10b981' : '#f59e0b',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {viberBusinessSent ? 'Yes' : 'No'}
          </span>
        );
      }
    },
    {
      accessorKey: 'viberBusinessSentAt',
      header: 'Viber Sent At',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'paidAt',
      header: 'Paid At',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'completedAt',
      header: 'Completed At',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: info => {
        const date = new Date(info.getValue());
        return date.toLocaleString();
      }
    },
    {
      accessorKey: 'soapLogs.calculateOrderPrice',
      header: 'SOAP: Calculate Price',
      cell: info => {
        const order = info.row.original;
        const log = order.soapLogs?.calculateOrderPrice;
        if (!log) return '-';
        return (
          <button
            onClick={() => {
              setSelectedSoapLog({ ...log, operation: 'calculateOrderPrice', orderId: order.orderId });
              setSoapLogModalOpen(true);
            }}
            style={{
              padding: '0.25rem 0.75rem',
              background: log.success ? '#10b981' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              textDecoration: 'underline'
            }}
          >
            {log.success ? '✓ View' : '✗ View'}
          </button>
        );
      }
    },
    {
      accessorKey: 'soapLogs.createReservedOrder',
      header: 'SOAP: Create Reserved',
      cell: info => {
        const order = info.row.original;
        const log = order.soapLogs?.createReservedOrder;
        if (!log) return '-';
        return (
          <button
            onClick={() => {
              setSelectedSoapLog({ ...log, operation: 'createReservedOrder', orderId: order.orderId });
              setSoapLogModalOpen(true);
            }}
            style={{
              padding: '0.25rem 0.75rem',
              background: log.success ? '#10b981' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              textDecoration: 'underline'
            }}
          >
            {log.success ? '✓ View' : '✗ View'}
          </button>
        );
      }
    },
    {
      accessorKey: 'paymentDetails',
      header: 'LiqPay Details',
      cell: info => {
        const order = info.row.original;
        const paymentDetails = order.paymentDetails;
        if (!paymentDetails || Object.keys(paymentDetails).length === 0) return '-';
        return (
          <button
            onClick={() => {
              setSelectedPaymentDetails({ ...paymentDetails, orderId: order.orderId });
              setPaymentDetailsModalOpen(true);
            }}
            style={{
              padding: '0.25rem 0.75rem',
              background: paymentDetails.liqpayStatus === 'success' ? '#10b981' : 
                         paymentDetails.liqpayStatus === 'failed' ? '#ef4444' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              textDecoration: 'underline'
            }}
          >
            View
          </button>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: info => {
        const order = info.row.original;
        const orderId = order.orderId;
        const isSendingEmail = sendingEmails.has(orderId);
        const isSendingTelegram = sendingTelegram.has(orderId);
        const isSendingViberBusiness = sendingViberBusiness.has(orderId);
        const hasConfirmationNumber = order.confirmationNumber;
        const hasEmail = order.customerInfo?.email;
        const hasPhone = order.customerInfo?.phone;
        const isOrderSuccess = order.isPaid || order.status === 'success' || order.status === 'paid' || order.status === 'completed';
        const hasTelegramConfig = messengerConfig && messengerConfig.telegram;
        const isTelegramEnabledInSettings = !settings || settings.telegram_enabled === null || settings.telegram_enabled !== false;
        const showTelegramButton = hasTelegramConfig && isTelegramEnabledInSettings;
        const hasViberBusinessConfig = messengerConfig && messengerConfig.viberBusiness;
        const isViberBusinessEnabledInSettings = !settings || settings.viber_business_enabled === null || settings.viber_business_enabled !== false;
        const showViberBusinessButton = hasViberBusinessConfig && isViberBusinessEnabledInSettings;
        
        const handleSendEmail = async () => {
          if (!hasConfirmationNumber || !hasEmail) {
            alert('Cannot send email: missing confirmation number or customer email');
            return;
          }
          
          setSendingEmails(prev => new Set(prev).add(orderId));
          
          try {
            const response = await authenticatedFetch(`/api/orders/${orderId}/send-email`, {
              method: 'POST'
            }, () => {
              handleUnauthorized();
              navigate('/admin/login');
            });
            
            const { data } = await parseJsonResponse(response);
            
            if (data && data.success) {
              alert('Email sent successfully!');
              fetchOrders();
            } else {
              alert(`Failed to send email: ${data?.message || data?.error || 'Unknown error'}`);
            }
          } catch (error) {
            console.error('Error sending email:', error);
            alert(`Error sending email: ${error.message}`);
          } finally {
            setSendingEmails(prev => {
              const newSet = new Set(prev);
              newSet.delete(orderId);
              return newSet;
            });
          }
        };
        
        const handleSendTelegram = async () => {
          if (!hasConfirmationNumber || !hasPhone) {
            alert('Cannot send Telegram message: missing confirmation number or customer phone');
            return;
          }
          
          setSendingTelegram(prev => new Set(prev).add(orderId));
          
          try {
            const response = await authenticatedFetch(`/api/telegram/send-qr/${orderId}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({})
            }, () => {
              handleUnauthorized();
              navigate('/admin/login');
            });
            
            const { data } = await parseJsonResponse(response);
            
            if (data && data.success) {
              alert('Telegram message sent successfully!');
              fetchOrders();
            } else {
              alert(`Failed to send Telegram message: ${data?.message || data?.error || 'Unknown error'}`);
            }
          } catch (error) {
            console.error('Error sending Telegram message:', error);
            alert(`Error sending Telegram message: ${error.message}`);
          } finally {
            setSendingTelegram(prev => {
              const newSet = new Set(prev);
              newSet.delete(orderId);
              return newSet;
            });
          }
        };
        
        const handleSendViberBusiness = async () => {
          if (!hasConfirmationNumber || !hasPhone) {
            alert('Cannot send Viber Business message: missing confirmation number or customer phone');
            return;
          }
          
          setSendingViberBusiness(prev => new Set(prev).add(orderId));
          
          try {
            const response = await authenticatedFetch(`/api/orders/${orderId}/send-viber-business`, {
              method: 'POST'
            }, () => {
              handleUnauthorized();
              navigate('/admin/login');
            });
            
            const { data } = await parseJsonResponse(response);
            
            if (data && data.success) {
              alert('Viber Business message sent successfully!');
              fetchOrders();
            } else {
              alert(`Failed to send Viber Business message: ${data?.message || data?.error || 'Unknown error'}`);
            }
          } catch (error) {
            console.error('Error sending Viber Business message:', error);
            alert(`Error sending Viber Business message: ${error.message}`);
          } finally {
            setSendingViberBusiness(prev => {
              const newSet = new Set(prev);
              newSet.delete(orderId);
              return newSet;
            });
          }
        };
        
        const isTelegramDisabled = isSendingTelegram || !hasConfirmationNumber || !hasPhone || !isOrderSuccess || !showTelegramButton;
        const isViberBusinessDisabled = isSendingViberBusiness || !hasConfirmationNumber || !hasPhone || !isOrderSuccess || !showViberBusinessButton;
        
        return (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleSendTelegram}
              disabled={isTelegramDisabled}
              style={{
                padding: '0.5rem 1rem',
                background: isSendingTelegram ? '#9ca3af' : (!showTelegramButton ? '#d1d5db' : (hasConfirmationNumber && hasPhone && isOrderSuccess ? '#0088cc' : '#d1d5db')),
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isTelegramDisabled ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {isSendingTelegram ? 'Sending...' : 'Send Message'}
            </button>
            <button
              onClick={handleSendViberBusiness}
              disabled={isViberBusinessDisabled}
              style={{
                padding: '0.5rem 1rem',
                background: isSendingViberBusiness ? '#9ca3af' : (!showViberBusinessButton ? '#d1d5db' : (hasConfirmationNumber && hasPhone && isOrderSuccess ? '#665cac' : '#d1d5db')),
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isViberBusinessDisabled ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {isSendingViberBusiness ? 'Sending...' : 'Send Viber'}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !hasConfirmationNumber || !hasEmail}
              style={{
                padding: '0.5rem 1rem',
                background: isSendingEmail ? '#9ca3af' : (hasConfirmationNumber && hasEmail ? '#3b82f6' : '#d1d5db'),
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSendingEmail || (!hasConfirmationNumber || !hasEmail) ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {isSendingEmail ? 'Sending...' : 'Send Mail'}
            </button>
          </div>
        );
      }
    }
  ];

  const contactsColumns = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: info => <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{info.getValue() || '-'}</span>
    },
    {
      accessorKey: 'firstName',
      header: 'First Name',
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'totalOrders',
      header: 'Total Orders',
      cell: info => {
        const count = info.getValue();
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: '#3b82f6',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {count || 0}
          </span>
        );
      }
    },
    {
      accessorKey: 'lastOrderDate',
      header: 'Last Order',
      cell: info => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleString() : '-';
      }
    },
    {
      accessorKey: 'totalSpent',
      header: 'Total Spent',
      cell: info => {
        const amount = info.getValue();
        const currency = info.row.original.currency || 'UAH';
        const formattedCurrency = currency === 'UAH' ? 'грн' : currency;
        return amount ? `${amount.toFixed(2)} ${formattedCurrency}` : '-';
      }
    },
    {
      accessorKey: 'totalAttemptSpent',
      header: 'Total Attempt Spent',
      cell: info => {
        const amount = info.getValue();
        const currency = info.row.original.currency || 'UAH';
        const formattedCurrency = currency === 'UAH' ? 'грн' : currency;
        return amount ? `${amount.toFixed(2)} ${formattedCurrency}` : '-';
      }
    }
  ];

  const errorsColumns = [
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: info => {
        const date = new Date(info.getValue());
        return date.toLocaleString();
      }
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: info => {
        const source = info.getValue();
        const colors = {
          api: '#3b82f6',
          skidata: '#10b981',
          liqpay: '#f59e0b',
          database: '#ef4444',
          internal: '#6b7280',
          external: '#8b5cf6'
        };
        return (
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            background: colors[source] || '#6b7280',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}>
            {source}
          </span>
        );
      }
    },
    {
      accessorKey: 'error',
      header: 'Error Type',
      cell: info => <span style={{ fontFamily: 'monospace' }}>{info.getValue()}</span>
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: info => (
        <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {info.getValue()}
        </div>
      )
    },
    {
      accessorKey: 'endpoint',
      header: 'Endpoint',
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'statusCode',
      header: 'Status',
      cell: info => {
        const code = info.getValue();
        if (!code) return '-';
        const color = code >= 500 ? '#ef4444' : code >= 400 ? '#f59e0b' : '#10b981';
        return <span style={{ color, fontWeight: '600' }}>{code}</span>;
      }
    }
  ];

  const ordersTable = useReactTable({
    data: orders,
    columns: ordersColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: ordersPagination.pages || 0,
    state: {
      pagination: {
        pageIndex: ordersPagination.page - 1,
        pageSize: ordersPagination.limit
      }
    }
  });

  const errorsTable = useReactTable({
    data: errors,
    columns: errorsColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: errorsPagination.pages || 0,
    state: {
      pagination: {
        pageIndex: errorsPagination.page - 1,
        pageSize: errorsPagination.limit
      }
    }
  });

  const contactsTable = useReactTable({
    data: contacts,
    columns: contactsColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: contactsPagination.pages || 0,
    state: {
      pagination: {
        pageIndex: contactsPagination.page - 1,
        pageSize: contactsPagination.limit
      }
    }
  });

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleManualCheckPayments = async () => {
    setManualCheckLoading(true);
    setManualCheckStats(null);
    
    try {
      const response = await authenticatedFetch('/api/orders/manual-check-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const { data } = await parseJsonResponse(response);
        if (data && data.success) {
          setManualCheckStats(data.stats);
          fetchOrders();
          fetchStats();
        } else {
          alert(`Failed to check payments: ${data?.message || 'Unknown error'}`);
        }
      } else {
        alert(`Failed to check payments: ${response.statusText || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error checking payments:', error);
      alert(`Error checking payments: ${error.message}`);
    } finally {
      setManualCheckLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0 }}>Admin Dashboard</h1>
        <button
          onClick={handleLogout}
          style={{
            padding: '0.5rem 1rem',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Logout
        </button>
      </header>

      {/* Stats */}
      {stats && (
        <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div 
            onClick={() => {
              setActiveTab('orders');
              setOrderStatusFilter('');
              setPaymentStatusFilter('');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Orders</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>{stats.totalOrders}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('contacts');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Spent</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>{stats.totalSpent ? `${stats.totalSpent.toLocaleString('uk-UA')} грн` : '0 грн'}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('contacts');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Attempt Spent</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>{stats.totalAttemptSpent ? `${stats.totalAttemptSpent.toLocaleString('uk-UA')} грн` : '0 грн'}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('orders');
              setOrderStatusFilter('pending');
              setPaymentStatusFilter('');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Pending</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>{stats.pendingOrders}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('orders');
              setOrderStatusFilter('');
              setPaymentStatusFilter('success');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Paid</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>{stats.paidOrders}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('orders');
              setOrderStatusFilter('failed');
              setPaymentStatusFilter('');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Failed</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>{stats.failedOrders || 0}</div>
          </div>
          <div 
            onClick={() => {
              setActiveTab('orders');
              setDiscountFilter('discounted');
              setOrderStatusFilter('');
              setPaymentStatusFilter('');
              setOrdersPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Discount saved (партнерська знижка)</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>{stats.discountSaved != null ? `${Number(stats.discountSaved).toLocaleString('uk-UA')} грн` : '0 грн'}</div>
            {stats.discountedOrdersCount != null && stats.discountedOrdersCount > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{stats.discountedOrdersCount} orders · click to view</div>
            )}
          </div>
          <div 
            onClick={() => {
              setActiveTab('errors');
            }}
            style={{ 
              background: 'white', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Errors</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>{stats.totalErrors}</div>
          </div>
        </div>
      )}

      {/* Manual Check Payments Section - Visible for all tabs */}
      {activeTab !== 'settings' && (
        <div style={{ padding: '1rem 2rem', background: 'white', marginBottom: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>Manual Payment Check</h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                Reset attempts to 0 and re-check payment status for pending/failed orders (last 24 hours)
              </p>
            </div>
            <button
              onClick={handleManualCheckPayments}
              disabled={manualCheckLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: manualCheckLoading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: manualCheckLoading ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {manualCheckLoading ? (
                <>
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <span>🔄</span>
                  <span>Check Payments</span>
                </>
              )}
            </button>
          </div>
          
          {manualCheckStats && (
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: '#f9fafb', 
              borderRadius: '6px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem'
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Processed</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>{manualCheckStats.processed}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Success</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{manualCheckStats.success}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Still Failed</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{manualCheckStats.failed}</div>
              </div>
              {manualCheckStats.errors && manualCheckStats.errors.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Errors</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>{manualCheckStats.errors.length}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ padding: '0 2rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'orders' ? '#3b82f6' : 'white',
              color: activeTab === 'orders' ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'errors' ? '#3b82f6' : 'white',
              color: activeTab === 'errors' ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Error Logs
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'contacts' ? '#3b82f6' : 'white',
              color: activeTab === 'contacts' ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Contact Info
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'settings' ? '#3b82f6' : 'white',
              color: activeTab === 'settings' ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Settings
          </button>
        </div>

        {/* Period Tabs - Show for Orders, Error Logs, and Contact Info (not Settings) */}
        {activeTab !== 'settings' && API_CONFIG.showProdTabs && (
          <div style={{ 
            marginBottom: '1rem', 
            display: 'flex', 
            gap: '0.5rem'
          }}>
            <button
              onClick={() => {
                setCurrentPeriod('all');
                // Don't clear date range filter when changing period
                if (activeTab === 'orders') setOrdersPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'errors') setErrorsPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'contacts') setContactsPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: currentPeriod === 'all' ? '#3b82f6' : 'white',
                color: currentPeriod === 'all' ? 'white' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              All
            </button>
            <button
              onClick={() => {
                setCurrentPeriod('beforeProd');
                // Don't clear date range filter when changing period
                if (activeTab === 'orders') setOrdersPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'errors') setErrorsPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'contacts') setContactsPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: currentPeriod === 'beforeProd' ? '#3b82f6' : 'white',
                color: currentPeriod === 'beforeProd' ? 'white' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              Pre-Prod (08.12 - {preProdEndDateFormatted})
            </button>
            <button
              onClick={() => {
                setCurrentPeriod('prod');
                // Don't clear date range filter when changing period
                if (activeTab === 'orders') setOrdersPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'errors') setErrorsPagination(prev => ({ ...prev, page: 1 }));
                else if (activeTab === 'contacts') setContactsPagination(prev => ({ ...prev, page: 1 }));
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: currentPeriod === 'prod' ? '#3b82f6' : 'white',
                color: currentPeriod === 'prod' ? 'white' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              Prod ({prodStartDateFormatted})
            </button>
          </div>
        )}

        {/* Search for Orders */}
        {activeTab === 'orders' && (
          <>
            {/* Date Range Filter - Show for "All", "Pre-Prod", and "Prod" tabs */}
            {API_CONFIG.showProdTabs && (
              <DateRangeFilter 
                key="date-filter-orders"
                defaultPreset={!sharedDateRange ? 'all' : undefined}
                currentDateRange={sharedDateRange}
                onDateRangeChange={(range) => {
                  setSharedDateRange(range);
                  setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  setErrorsPagination(prev => ({ ...prev, page: 1 }));
                  setContactsPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            )}
            
            {/* Order Filters */}
            <div style={{ 
              marginBottom: '1rem', 
              marginTop: '1rem',
              display: 'flex', 
              gap: '0.75rem', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Status:
                <select
                  value={orderStatusFilter}
                  onChange={(e) => {
                    setOrderStatusFilter(e.target.value);
                    setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="">Всі</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
              
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Payment Status:
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => {
                    setPaymentStatusFilter(e.target.value);
                    setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="">Всі</option>
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </select>
              </label>
              
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Email Sent:
                <select
                  value={emailSentFilter}
                  onChange={(e) => {
                    setEmailSentFilter(e.target.value);
                    setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="">Всі</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                TG Sent:
                <select
                  value={telegramSentFilter}
                  onChange={(e) => {
                    setTelegramSentFilter(e.target.value);
                    setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="">Всі</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                Discount:
                <select
                  value={discountFilter}
                  onChange={(e) => {
                    setDiscountFilter(e.target.value);
                    setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  <option value="">All</option>
                  <option value="discounted">Discounted only</option>
                </select>
              </label>
            </div>
            
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="Search by Order ID, Email, Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setOrdersPagination(prev => ({ ...prev, page: 1 }));
                  fetchOrders();
                }
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
            <button
              onClick={() => {
                setOrdersPagination(prev => ({ ...prev, page: 1 }));
                fetchOrders();
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Search
            </button>
          </div>
          </>
        )}

        {/* Settings Tab Content */}
        {activeTab === 'settings' && (
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '2rem' }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>
              Feature Switches
            </h2>
            <p style={{ marginBottom: '2rem', color: '#6b7280', fontSize: '0.875rem' }}>
              Control selling functionality for different seasons. Changes take effect immediately and are broadcast to all connected clients.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Global Can Sell */}
              <div style={{ 
                padding: '1.5rem', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: settings.can_sell ? '#f0fdf4' : '#fef2f2'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                      Global Selling
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      When disabled, no selling is allowed for any season, regardless of individual season flags.
                    </p>
                  </div>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '60px', 
                    height: '34px',
                    cursor: updatingSettings ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.can_sell}
                      onChange={(e) => updateSetting('can_sell', e.target.checked)}
                      disabled={updatingSettings}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: settings.can_sell ? '#10b981' : '#ef4444',
                      borderRadius: '34px',
                      transition: '0.3s',
                      opacity: updatingSettings ? 0.5 : 1
                    }}>
                      <span style={{
                        position: 'absolute',
                        height: '26px',
                        width: '26px',
                        left: settings.can_sell ? '30px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              {/* Holiday Season */}
              <div style={{ 
                padding: '1.5rem', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: settings.can_sell_holiday_season ? '#f0fdf4' : '#fef2f2',
                opacity: !settings.can_sell ? 0.5 : 1
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                      Holiday Season
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Controls selling for Holiday season. Respects 5-day pre-close rule (has priority over this flag).
                    </p>
                  </div>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '60px', 
                    height: '34px',
                    cursor: (updatingSettings || !settings.can_sell) ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.can_sell_holiday_season}
                      onChange={(e) => updateSetting('can_sell_holiday_season', e.target.checked)}
                      disabled={updatingSettings || !settings.can_sell}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: settings.can_sell_holiday_season ? '#10b981' : '#ef4444',
                      borderRadius: '34px',
                      transition: '0.3s',
                      opacity: (updatingSettings || !settings.can_sell) ? 0.5 : 1
                    }}>
                      <span style={{
                        position: 'absolute',
                        height: '26px',
                        width: '26px',
                        left: settings.can_sell_holiday_season ? '30px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              {/* Winter Season */}
              <div style={{ 
                padding: '1.5rem', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: settings.can_sell_winter_season ? '#f0fdf4' : '#fef2f2',
                opacity: !settings.can_sell ? 0.5 : 1
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                      Winter Season
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Controls selling for Winter season. Respects 5-day pre-close rule (has priority over this flag).
                    </p>
                  </div>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '60px', 
                    height: '34px',
                    cursor: (updatingSettings || !settings.can_sell) ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.can_sell_winter_season}
                      onChange={(e) => updateSetting('can_sell_winter_season', e.target.checked)}
                      disabled={updatingSettings || !settings.can_sell}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: settings.can_sell_winter_season ? '#10b981' : '#ef4444',
                      borderRadius: '34px',
                      transition: '0.3s',
                      opacity: (updatingSettings || !settings.can_sell) ? 0.5 : 1
                    }}>
                      <span style={{
                        position: 'absolute',
                        height: '26px',
                        width: '26px',
                        left: settings.can_sell_winter_season ? '30px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              {/* Low Season */}
              <div style={{ 
                padding: '1.5rem', 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px',
                background: settings.can_sell_low_season ? '#f0fdf4' : '#fef2f2',
                opacity: !settings.can_sell ? 0.5 : 1
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                      Low Season
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Controls selling for Low season. Respects 5-day pre-close rule (has priority over this flag).
                    </p>
                  </div>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '60px', 
                    height: '34px',
                    cursor: (updatingSettings || !settings.can_sell) ? 'not-allowed' : 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.can_sell_low_season}
                      onChange={(e) => updateSetting('can_sell_low_season', e.target.checked)}
                      disabled={updatingSettings || !settings.can_sell}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: settings.can_sell_low_season ? '#10b981' : '#ef4444',
                      borderRadius: '34px',
                      transition: '0.3s',
                      opacity: (updatingSettings || !settings.can_sell) ? 0.5 : 1
                    }}>
                      <span style={{
                        position: 'absolute',
                        height: '26px',
                        width: '26px',
                        left: settings.can_sell_low_season ? '30px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>
                  Messengers
                </h2>

                <div style={{ 
                  padding: '1.5rem', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  background: settings.telegram_enabled !== false ? '#f0fdf4' : '#fef2f2'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                        Telegram
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        Enable/disable Telegram QR code delivery. If not set, uses TELEGRAM_ENABLED from .env
                      </p>
                    </div>
                    <label style={{ 
                      position: 'relative', 
                      display: 'inline-block', 
                      width: '60px', 
                      height: '34px',
                      cursor: updatingSettings ? 'not-allowed' : 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={settings.telegram_enabled !== false}
                        onChange={(e) => updateSetting('telegram_enabled', e.target.checked)}
                        disabled={updatingSettings}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: settings.telegram_enabled !== false ? '#10b981' : '#ef4444',
                        borderRadius: '34px',
                        transition: '0.3s',
                        opacity: updatingSettings ? 0.5 : 1
                      }}>
                        <span style={{
                          position: 'absolute',
                          height: '26px',
                          width: '26px',
                          left: settings.telegram_enabled !== false ? '30px' : '4px',
                          bottom: '4px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: '0.3s',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </span>
                    </label>
                  </div>
                </div>

                <div style={{ 
                  padding: '1.5rem', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px',
                  background: settings.viber_business_enabled !== false ? '#f0fdf4' : '#fef2f2',
                  marginTop: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                        Viber Business Chat
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        Enable/disable Viber Business Chat QR code delivery. If not set, uses VIBER_BUSINESS_ENABLED from .env
                      </p>
                    </div>
                    <label style={{ 
                      position: 'relative', 
                      display: 'inline-block', 
                      width: '60px', 
                      height: '34px',
                      cursor: updatingSettings ? 'not-allowed' : 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={settings.viber_business_enabled !== false}
                        onChange={(e) => updateSetting('viber_business_enabled', e.target.checked)}
                        disabled={updatingSettings}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: settings.viber_business_enabled !== false ? '#10b981' : '#ef4444',
                        borderRadius: '34px',
                        transition: '0.3s',
                        opacity: updatingSettings ? 0.5 : 1
                      }}>
                        <span style={{
                          position: 'absolute',
                          height: '26px',
                          width: '26px',
                          left: settings.viber_business_enabled !== false ? '30px' : '4px',
                          bottom: '4px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: '0.3s',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </span>
                    </label>
                  </div>
                </div>

                <div style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>
                    Partner discount (Softserve)
                  </h2>

                  <div style={{ 
                    padding: '1.5rem', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    background: settings.softserve_discount_enabled ? '#f0fdf4' : '#fef2f2'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                          Softserve partner discount
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          Enable/disable partner discount for @softserveinc.com emails. When enabled, eligible users can request a discount code and apply it at checkout.
                        </p>
                      </div>
                      <label style={{ 
                        position: 'relative', 
                        display: 'inline-block', 
                        width: '60px', 
                        height: '34px',
                        cursor: updatingSettings ? 'not-allowed' : 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={!!settings.softserve_discount_enabled}
                          onChange={(e) => updateSetting('softserve_discount_enabled', e.target.checked)}
                          disabled={updatingSettings}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: settings.softserve_discount_enabled ? '#10b981' : '#ef4444',
                          borderRadius: '34px',
                          transition: '0.3s',
                          opacity: updatingSettings ? 0.5 : 1
                        }}>
                          <span style={{
                            position: 'absolute',
                            height: '26px',
                            width: '26px',
                            left: settings.softserve_discount_enabled ? '30px' : '4px',
                            bottom: '4px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            transition: '0.3s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </span>
                      </label>
                    </div>
                    {settings.softserve_discount_enabled && (
                      <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          Discount percent:
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={typeof settings.softserve_discount_percent === 'number' ? settings.softserve_discount_percent : 10}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 0 && v <= 100) updateSetting('softserve_discount_percent', v);
                          }}
                          disabled={updatingSettings}
                          style={{ width: '80px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>% (0–100)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {settings.updatedAt && (
              <div style={{ marginTop: '2rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem', color: '#6b7280' }}>
                Last updated: {new Date(settings.updatedAt).toLocaleString()}
                {settings.updatedBy && ` by ${settings.updatedBy}`}
              </div>
            )}
          </div>
        )}

        {/* Date Range Filters - Show for "All", "Pre-Prod", and "Prod" tabs */}
        {activeTab === 'errors' && API_CONFIG.showProdTabs && (
          <DateRangeFilter 
            key="date-filter-errors"
            defaultPreset={!sharedDateRange ? 'all' : undefined}
            currentDateRange={sharedDateRange}
            onDateRangeChange={(range) => {
              setSharedDateRange(range);
              setOrdersPagination(prev => ({ ...prev, page: 1 }));
              setErrorsPagination(prev => ({ ...prev, page: 1 }));
              setContactsPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
        )}
        {activeTab === 'contacts' && API_CONFIG.showProdTabs && (
          <DateRangeFilter 
            key="date-filter-contacts"
            defaultPreset={!sharedDateRange ? 'all' : undefined}
            currentDateRange={sharedDateRange}
            onDateRangeChange={(range) => {
              setSharedDateRange(range);
              setOrdersPagination(prev => ({ ...prev, page: 1 }));
              setErrorsPagination(prev => ({ ...prev, page: 1 }));
              setContactsPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
        )}

        {/* Table */}
        {activeTab !== 'settings' && (
          <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>Loading...</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      {(activeTab === 'orders' ? ordersTable : activeTab === 'errors' ? errorsTable : contactsTable).getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            style={{
                              padding: '1rem',
                              textAlign: 'left',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              color: '#374151',
                              cursor: header.column.getCanSort() ? 'pointer' : 'default'
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ↑',
                              desc: ' ↓',
                            }[header.column.getIsSorted()] ?? null}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {(activeTab === 'orders' ? ordersTable : activeTab === 'errors' ? errorsTable : contactsTable).getRowModel().rows.map(row => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} style={{ padding: '1rem', fontSize: '0.875rem' }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Showing {((activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page - 1) * (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).limit + 1} to{' '}
                  {Math.min((activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page * (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).limit, (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).total)} of{' '}
                  {(activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).total} results
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      const pagination = activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination;
                      if (pagination.page > 1) {
                        if (activeTab === 'orders') {
                          setOrdersPagination(prev => ({ ...prev, page: prev.page - 1 }));
                        } else if (activeTab === 'errors') {
                          setErrorsPagination(prev => ({ ...prev, page: prev.page - 1 }));
                        } else {
                          setContactsPagination(prev => ({ ...prev, page: prev.page - 1 }));
                        }
                      }
                    }}
                    disabled={(activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      background: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page === 1 ? '#e5e7eb' : '#3b82f6',
                      color: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page === 1 ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => {
                      const pagination = activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination;
                      if (pagination.page < pagination.pages) {
                        if (activeTab === 'orders') {
                          setOrdersPagination(prev => ({ ...prev, page: prev.page + 1 }));
                        } else if (activeTab === 'errors') {
                          setErrorsPagination(prev => ({ ...prev, page: prev.page + 1 }));
                        } else {
                          setContactsPagination(prev => ({ ...prev, page: prev.page + 1 }));
                        }
                      }
                    }}
                    disabled={(activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page >= (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).pages}
                    style={{
                      padding: '0.5rem 1rem',
                      background: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page >= (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).pages ? '#e5e7eb' : '#3b82f6',
                      color: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page >= (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).pages ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).page >= (activeTab === 'orders' ? ordersPagination : activeTab === 'errors' ? errorsPagination : contactsPagination).pages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* SOAP Log Modal */}
      {soapLogModalOpen && selectedSoapLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={() => setSoapLogModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              width: '100%',
              maxWidth: '1200px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                SOAP Log: {selectedSoapLog.operation}
              </h2>
              <button
                onClick={() => setSoapLogModalOpen(false)}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Order ID</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: '500' }}>{selectedSoapLog.orderId}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Status</div>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    background: selectedSoapLog.success ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}>
                    {selectedSoapLog.success ? '✓ Success' : '✗ Failed'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Timestamp</div>
                  <div>{selectedSoapLog.timestamp ? new Date(selectedSoapLog.timestamp).toLocaleString() : '-'}</div>
                </div>
                {selectedSoapLog.error && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Error</div>
                    <div style={{ color: '#ef4444', fontWeight: '500' }}>{selectedSoapLog.error}</div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1.125rem', fontWeight: '600' }}>SOAP Request</h3>
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '1rem',
                  overflow: 'auto',
                  maxHeight: '400px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {selectedSoapLog.request || '-'}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1.125rem', fontWeight: '600' }}>SOAP Response</h3>
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '1rem',
                  overflow: 'auto',
                  maxHeight: '400px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {selectedSoapLog.response || '-'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {paymentDetailsModalOpen && selectedPaymentDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setPaymentDetailsModalOpen(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                LiqPay Payment Details
              </h2>
              <button
                onClick={() => setPaymentDetailsModalOpen(false)}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Order ID</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: '500' }}>{selectedPaymentDetails.orderId}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>LiqPay Status</div>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    background: selectedPaymentDetails.liqpayStatus === 'success' ? '#10b981' : 
                               selectedPaymentDetails.liqpayStatus === 'failed' ? '#ef4444' : '#3b82f6',
                    color: 'white',
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}>
                    {selectedPaymentDetails.liqpayStatus || '-'}
                  </div>
                </div>
                {selectedPaymentDetails.transactionId && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Transaction ID</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{selectedPaymentDetails.transactionId}</div>
                  </div>
                )}
                {selectedPaymentDetails.paymentId && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Payment ID</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{selectedPaymentDetails.paymentId}</div>
                  </div>
                )}
                {selectedPaymentDetails.amount && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Amount</div>
                    <div style={{ fontWeight: '500' }}>{selectedPaymentDetails.amount} {selectedPaymentDetails.currency || 'UAH'}</div>
                  </div>
                )}
                {selectedPaymentDetails.checkedAt && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Checked At</div>
                    <div>{new Date(selectedPaymentDetails.checkedAt).toLocaleString()}</div>
                  </div>
                )}
                {selectedPaymentDetails.processedAt && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Processed At</div>
                    <div>{new Date(selectedPaymentDetails.processedAt).toLocaleString()}</div>
                  </div>
                )}
                {selectedPaymentDetails.checkedViaAPI && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Checked Via API</div>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '6px',
                      background: '#10b981',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}>
                      Yes
                    </div>
                  </div>
                )}
                {selectedPaymentDetails.finalFailure && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Final Failure</div>
                    <div style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '6px',
                      background: '#ef4444',
                      color: 'white',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}>
                      Yes
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '1.125rem', fontWeight: '600' }}>Full Payment Details (JSON)</h3>
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '1rem',
                  overflow: 'auto',
                  maxHeight: '400px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {JSON.stringify(selectedPaymentDetails, null, 2)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

